import * as io from "io-ts";
import React, { useCallback, useRef } from "react";
import Modal from "@ui/ModalV2";
import t from "../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useSettings } from "../../../api/useSettings";
import { ActionLink } from "@ui/Link";
import Checkbox from "@ui/Checkbox";
import createGlobalState from "@ui/lib/createGlobalState";
import { Markdown } from "@ui/Markdown";
import { MTS_ENV } from "../../../lib/setupEnv";

const useHasReadLegals = createGlobalState({
  storage: "local",
  key: "hasReadLegals",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

const useHasSeenLegals = createGlobalState({
  storage: "session",
  key: "hasSeenLegals",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

interface P {
  openModal: boolean;
  closeModal: () => void;
}

const Legals = ({ closeModal, openModal }: P) => {
  const settings = useSettings();
  const [hasSeenLegals, setHasSeenLegals] = useHasSeenLegals({});
  const [hasReadLegals, setHasReadLegals] = useHasReadLegals({});
  const hasReadLegalsRef = useRef(hasReadLegals);

  const close = useCallback(() => {
    setHasSeenLegals(true);
    return Boolean(closeModal());
  }, [closeModal, setHasSeenLegals]);

  // if hasReadLegals, stay invisible unless user flush its local storage.
  if (hasSeenLegals || hasReadLegalsRef.current || settings.LEGAL_TEXT.length === 0) {
    return null;
  }

  return (
    <Modal
      size="m"
      title={t("app.modal.legals.title", new Date().getFullYear().toString())}
      isOpen={openModal}
      onRequestClose={close}
    >
      <ModalForm onRequestClose={close} onSubmit={close}>
        <Markdown defaultValue={settings.LEGAL_TEXT} />
        {settings.LEGAL_FILE_PATH.length > 0 && (
          <ActionLink
            href={MTS_ENV.COREV2_ASSETS_URL + "/" + settings.LEGAL_FILE_PATH}
            target="_blank"
          >
            {t("app.legals_modal.legals_link")}
          </ActionLink>
        )}
        <Checkbox onChange={e => setHasReadLegals(e.target.checked)} checked={hasReadLegals}>
          {t("app.legals_modal.skip_message")}
        </Checkbox>
      </ModalForm>
    </Modal>
  );
};

export default Legals;
